// Recherche de disponibilités — logique partagée entre l'écran Search (bouton
// « Recherche ») et le bouton « Réessayer » de Results. Navigation OPTIMISTE :
// on affiche Results (skeleton) AVANT la réponse API, puis on remplit les
// résultats — ou on pose store.error (état d'erreur dédié sur Results).
import { useStore } from '../store'
import { useApi } from '../api'
import { useRouter } from '../router'
import { useTracker } from '../tracker'
import { routeKey } from '../utils/track-meta'
import { hasPets } from '../../../shared/rules'
import { BookingState, BookingStep, Direction } from '../../../shared/types/enums'
import type { PayloadRequestItem, RequestItem } from '../../../shared/types'

export function useSearch() {
  const store = useStore()
  const api = useApi()
  const router = useRouter()
  const tracker = useTracker()

  /**
   * Cherche la 1ère heure de départ pour un journey à une date donnée. Le store
   * contient les `departure[]` chargés au pick du journey via getJourneyVariants.
   */
  function findDepartureTime(journeyId: string | undefined, date: string | undefined): string | undefined {
    if (!journeyId || !date) return undefined
    const journey = store.search.journeys.find((j) => j.id === journeyId)
    if (!journey?.departure) return undefined
    // Trie chronologiquement et prend la 1ère matching la date.
    const matching = journey.departure
      .filter((d) => d?.depart === date && d.time)
      .sort((a, b) => (a.time ?? '').localeCompare(b.time ?? ''))
    return matching[0]?.time
  }

  function buildPayload(): PayloadRequestItem[] {
    const isReturn = store.search.direction === Direction.Return
    const petsPresent = hasPets(store.search.pets)
    const items: PayloadRequestItem[] = []
    for (const [company, requestItems] of Object.entries(store.search.request)) {
      for (const req of requestItems as RequestItem[]) {
        // out_time/ret_time : 1ère heure de départ trouvée pour ce journey à
        // cette date. Backend exige le champ rempli (mandatory).
        const baseRequest: RequestItem = {
          ...req,
          company,
          out_time: req.out_time || findDepartureTime(req.out_journey_id, req.out_at) || '',
          ret_time: req.ret_time || findDepartureTime(req.ret_journey_id, req.ret_at) || '',
        }
        const request: RequestItem = isReturn
          ? baseRequest
          : { ...baseRequest, ret_journey_id: undefined, ret_at: '', ret_time: '', ret_is_open: false }
        const item: PayloadRequestItem = {
          request,
          passengers: store.search.passengers,
          vehicle: store.search.vehicle.code
            ? {
                code: store.search.vehicle.code,
                roof: store.search.vehicle.roof,
                trailer: store.search.vehicle.trailer,
              }
            : null,
        }
        if (petsPresent) item.pets = store.search.pets
        items.push(item)
      }
    }
    return items
  }

  /**
   * Lance la recherche. Navigation OPTIMISTE : `goTo(Results)` AVANT l'await, la
   * page affiche son skeleton pendant l'appel, puis les résultats (ou l'erreur).
   * Réutilisé par Search.submit() et le « Réessayer » de Results.
   */
  async function run(): Promise<void> {
    if (store.loading || store.loadingJourney) return
    store.loading = true
    store.error = null
    store.state = BookingState.Searching

    // Jalon funnel : l'user a lancé une recherche (entrée dans le tunnel).
    // reset() réarme les jalons « once » pour ce nouveau parcours.
    tracker.reset()
    tracker.emit('search_submitted', {
      route: routeKey(store.search.journeys),
      direction: store.search.direction,
      passengers: store.search.passengers.length,
    })

    // Affiche Results (skeleton) tout de suite ; l'appel continue en fond.
    router.goTo(BookingStep.Results)

    try {
      const result = await api.searchAvailability(buildPayload())
      store.availabilities = {
        outward: { primary: result.outward[0] ?? null, all: result.outward },
        return: result.return.length > 0
          ? { primary: result.return[0], all: result.return }
          : { primary: undefined, all: [] },
      }
      store.state = BookingState.TicketsSelected
      // Jalon funnel : des résultats sont affichés.
      tracker.emit('results_viewed', {
        count: store.availabilities.outward.all?.length ?? 0,
        route: routeKey(store.search.journeys),
      })
    } catch (err) {
      store.error = { code: 'SEARCH_FAILED', message: err instanceof Error ? err.message : 'Search failed' }
    } finally {
      store.loading = false
    }
  }

  return { run }
}
